import React from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material'
import {
  Language as LanguageIcon,
  Logout as LogoutIcon,
  Info as InfoIcon,
  BugReport as BugReportIcon,
  PrivacyTip as PrivacyTipIcon,
  Business as BusinessIcon,
  Print as PrintIcon,
  Cable as CableIcon,
  Straighten as StraightenIcon,
  LocalPrintshop as LocalPrintshopIcon,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { FormattedMessage } from 'react-intl'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'

import { useLanguage } from '../intl-provider/IntlProvider'
import { formatVersionForDisplay } from '../../utils/version'
import { formatLogBuffer } from '../../services/logger/logBuffer'
import { useOrganizationSwitchDialog } from '../../hooks/useOrganizationSwitchDialog'
import FileFormats from '../../constants/FileFormats'
import PrinterTransports from '../../constants/PrinterTransports'
import {
  getAvailableLabelFormats,
  getLabelFormat,
  setLabelFormat,
} from '../../services/label-format/labelFormat'
import {
  getAvailableTransports,
  getPreferredTransport,
  resolveTransport,
  setPreferredTransport,
} from '../../services/printer/printerSettings'
import { usePrinterSelectDialog } from '../../hooks/usePrinterSelectDialog'
import { isLabelPrinterAvailable } from '../../services/print/labelPrinter'
import {
  getAvailableLabelSizes,
  getLabelSize,
  setLabelSize,
} from '../../services/label-size/labelSize'

export interface IOwnProps {
  open: boolean
  onClose: () => void
}

export interface IDispatchProps {
  logout: () => void
}

export type IProps = IOwnProps & IDispatchProps

const DrawerContent = styled(Box)(({ theme }) => ({
  width: 280,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.backgroundColor.main,
  color: '#d4d4d3',
  height: '100%',
}))

const SectionHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1, 2),
  fontWeight: 600,
  fontSize: '0.875rem',
  color: '#d4d4d3',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}))

const StyledListItemText = styled(ListItemText)(() => ({
  '& .MuiListItemText-primary': {
    color: '#d4d4d3',
  },
  '& .MuiListItemText-secondary': {
    color: '#a0a0a0',
  },
}))

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  color: '#d4d4d3',
}))

const PRIVACY_POLICY_URL = 'https://privacy.deliverider.com/sellerapp.html'

const TRANSPORT_LABELS: Record<PrinterTransports, React.ReactNode> = {
  [PrinterTransports.AUTO]: (
    <FormattedMessage
      id='app.components.app-drawer.printer-connection.auto'
      defaultMessage='Automatic'
    />
  ),
  [PrinterTransports.BLUETOOTH]: (
    <FormattedMessage
      id='app.components.app-drawer.printer-connection.bluetooth'
      defaultMessage='Bluetooth'
    />
  ),
  [PrinterTransports.USB]: (
    <FormattedMessage
      id='app.components.app-drawer.printer-connection.usb'
      defaultMessage='USB cable'
    />
  ),
}

const LABEL_FORMAT_LABELS: Record<FileFormats, React.ReactNode> = {
  [FileFormats.ZPL]: (
    <FormattedMessage
      id='app.components.app-drawer.label-format.zpl'
      defaultMessage='Label printer (4x4)'
    />
  ),
  [FileFormats.PDF]: (
    <FormattedMessage
      id='app.components.app-drawer.label-format.pdf'
      defaultMessage='PDF document'
    />
  ),
}


/**
 * Copies text without a native clipboard plugin, so this ships over OTA rather
 * than needing a new APK. navigator.clipboard is available because the WebView
 * origin (http://localhost) counts as a secure context, but it still requires a
 * user gesture and can be refused, hence the textarea fallback.
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      return copied
    } catch {
      return false
    }
  }
}

const AppDrawer: React.FC<IProps> = ({ open, onClose, logout }) => {
  const { locale, setLocale, availableLanguages } = useLanguage()
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'failed'>('idle')
  const { selectedOrganization, open: openOrganizationDialog, dialog: organizationDialog } = useOrganizationSwitchDialog()
  // Seeded from storage rather than held only in React state — the drawer is
  // keepMounted, so this initialiser runs once and must reflect what the print path
  // will actually read, not a default that drifts from it.
  const [labelFormat, setLabelFormatState] = React.useState<FileFormats>(getLabelFormat)
  const [transport, setTransportState] = React.useState<PrinterTransports>(getPreferredTransport)
  const { selectedBluetoothName, open: openPrinterDialog, dialog: printerDialog } = usePrinterSelectDialog()
  const [resolvedTransport, setResolvedTransport] = React.useState<PrinterTransports>()
  const [labelSize, setLabelSizeState] = React.useState<string>(getLabelSize)

  /**
   * Automatic prefers a permitted USB cable over the chosen Bluetooth printer, so the
   * stored Bluetooth name is not necessarily where the next label goes. Resolve the
   * real target while the drawer is open so the row cannot name a printer that will
   * not be used.
   */
  React.useEffect(() => {
    if (!open || !isLabelPrinterAvailable() || labelFormat !== FileFormats.ZPL) {
      return
    }

    let isStale = false

    const resolve = () => {
      resolveTransport()
        .then(target => {
          if (!isStale) {
            setResolvedTransport(target)
          }
        })
        .catch(() => undefined)
    }

    resolve()

    // Plugging the cable in while the drawer is open raises the system USB dialog and
    // backgrounds the app, so re-resolve on resume — otherwise the row keeps naming
    // the Bluetooth printer until the drawer is closed and reopened.
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        resolve()
      }
    })

    return () => {
      isStale = true
      listener.then(handle => handle.remove()).catch(() => undefined)
    }
  }, [open, labelFormat, transport, selectedBluetoothName])

  const handleCopyLogs = async () => {
    const logs = formatLogBuffer()
    const copied = logs ? await copyToClipboard(logs) : false

    setCopyState(copied ? 'copied' : 'failed')
    // The drawer stays open so the result is visible; reset so a second tap reads
    // as a fresh action rather than a stale "Copied".
    window.setTimeout(() => setCopyState('idle'), 2500)
  }

  const handleLanguageChange = async () => {
    try {
      // Toggle between available languages
      const currentIndex = availableLanguages.findIndex(lang => lang.code === locale)
      const nextIndex = (currentIndex + 1) % availableLanguages.length
      const nextLanguage = availableLanguages[nextIndex]

      await setLocale(nextLanguage.code)
      onClose()
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }

  const handleLabelFormatChange = () => {
    // Cycles rather than opening a picker, matching the language row above. No
    // reload is needed: getLabelFormat() is re-read on every print.
    const formats = getAvailableLabelFormats()
    const nextFormat = formats[(formats.indexOf(labelFormat) + 1) % formats.length]

    setLabelFormat(nextFormat)
    setLabelFormatState(nextFormat)
  }

  const handleLabelSizeChange = () => {
    const sizes = getAvailableLabelSizes()
    const nextSize = sizes[(sizes.indexOf(labelSize) + 1) % sizes.length]

    setLabelSize(nextSize)
    setLabelSizeState(nextSize)
  }

  const handleTransportChange = () => {
    const transports = getAvailableTransports()
    const nextTransport = transports[(transports.indexOf(transport) + 1) % transports.length]

    setPreferredTransport(nextTransport)
    setTransportState(nextTransport)
  }

  const handleLogout = () => {
    logout()
    onClose()
  }

  const handleOpenPrivacyPolicy = () => {
    window.open(PRIVACY_POLICY_URL, '_blank')
  }

  const currentLanguage = availableLanguages.find(lang => lang.code === locale)

  return (
    <>
    <Drawer
      anchor='left'
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better performance on mobile
      }}
    >
      <DrawerContent role='presentation'>
        {/* Settings Section */}
        <SectionHeader>
          <FormattedMessage
            id='app.components.app-drawer.settings'
            defaultMessage='Settings'
          />
        </SectionHeader>

        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLanguageChange}>
              <StyledListItemIcon>
                <LanguageIcon />
              </StyledListItemIcon>
              <StyledListItemText
                primary={
                  <FormattedMessage
                    id='app.components.app-drawer.language'
                    defaultMessage='Language'
                  />
                }
                secondary={currentLanguage?.nativeName}
              />
            </ListItemButton>
          </ListItem>

        </List>

        <Divider />

        {/* Printer Section */}
        <SectionHeader>
          <FormattedMessage
            id='app.components.app-drawer.printer'
            defaultMessage='Printer'
          />
        </SectionHeader>

        <List>
          {/* Shown on web too: the format is a property of the printer the seller has
              attached, not of the platform. The platform only decides the default. */}
          <ListItem disablePadding>
            <ListItemButton onClick={handleLabelFormatChange}>
              <StyledListItemIcon>
                <PrintIcon />
              </StyledListItemIcon>
              <StyledListItemText
                primary={
                  <FormattedMessage
                    id='app.components.app-drawer.label-format'
                    defaultMessage='Label format'
                  />
                }
                secondary={LABEL_FORMAT_LABELS[labelFormat]}
              />
            </ListItemButton>
          </ListItem>

          {/* Gated on the plugin actually being present, not merely on being native —
              the same condition printDocumentByZpl routes on. During the OTA-before-APK
              window these settings would otherwise be offered while the legacy Cordova
              fallback silently ignores both, printing to the first paired device.
              Only meaningful while ZPL is the selected format — PDF goes to Android's
              own print dialog, which picks its own destination. */}
          {isLabelPrinterAvailable() && labelFormat === FileFormats.ZPL && (
            <>
              {/* Physical stock in the printer. Sent to the server with the printer's
                  detected dpi, since a size without a resolution renders at the wrong
                  scale — see LabelApiDataHandler. */}
              <ListItem disablePadding>
                <ListItemButton onClick={handleLabelSizeChange}>
                  <StyledListItemIcon>
                    <StraightenIcon />
                  </StyledListItemIcon>
                  <StyledListItemText
                    primary={
                      <FormattedMessage
                        id='app.components.app-drawer.label-size'
                        defaultMessage='Label size'
                      />
                    }
                    secondary={`${labelSize} in`}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={handleTransportChange}>
                  <StyledListItemIcon>
                    <CableIcon />
                  </StyledListItemIcon>
                  <StyledListItemText
                    primary={
                      <FormattedMessage
                        id='app.components.app-drawer.printer-connection'
                        defaultMessage='Connection'
                      />
                    }
                    secondary={TRANSPORT_LABELS[transport]}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={openPrinterDialog}>
                  <StyledListItemIcon>
                    <LocalPrintshopIcon />
                  </StyledListItemIcon>
                  <StyledListItemText
                    primary={
                      <FormattedMessage
                        id='app.components.app-drawer.printer-device'
                        defaultMessage='Printer'
                      />
                    }
                    secondary={
                      // Shows the cable when that is what will actually be used,
                      // rather than a Bluetooth name Automatic is about to override.
                      resolvedTransport === PrinterTransports.USB
                        ? TRANSPORT_LABELS[PrinterTransports.USB]
                        : selectedBluetoothName ?? (
                          <FormattedMessage
                            id='app.components.app-drawer.printer-device.automatic'
                            defaultMessage='First available'
                          />
                        )
                    }
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>

        <Divider />

        {/* App Information Section */}
        <SectionHeader>
          <FormattedMessage
            id='app.components.app-drawer.app-info'
            defaultMessage='App Information'
          />
        </SectionHeader>

        <List>
          <ListItem disablePadding>
            <ListItemButton>
              <StyledListItemIcon>
                <InfoIcon />
              </StyledListItemIcon>
              <StyledListItemText
                primary={
                  <FormattedMessage
                    id='app.components.app-drawer.version'
                    defaultMessage='Version'
                  />
                }
                secondary={formatVersionForDisplay()}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={handleCopyLogs}>
              <StyledListItemIcon>
                <BugReportIcon />
              </StyledListItemIcon>
              <StyledListItemText
                primary={
                  <FormattedMessage
                    id='app.components.app-drawer.copy-logs'
                    defaultMessage='Copy diagnostic logs'
                  />
                }
                secondary={
                  copyState === 'copied' ? (
                    <FormattedMessage
                      id='app.components.app-drawer.copy-logs.copied'
                      defaultMessage='Copied to clipboard'
                    />
                  ) : copyState === 'failed' ? (
                    <FormattedMessage
                      id='app.components.app-drawer.copy-logs.failed'
                      defaultMessage='Nothing to copy'
                    />
                  ) : (
                    <FormattedMessage
                      id='app.components.app-drawer.copy-logs.hint'
                      defaultMessage='Send these to support'
                    />
                  )
                }
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider />

        {/* Organization Section — native only; web deploys are fixed single-tenant */}
        {Capacitor.isNativePlatform() && (
          <>
            <SectionHeader>
              <FormattedMessage
                id='app.components.app-drawer.organization'
                defaultMessage='Organization'
              />
            </SectionHeader>

            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={openOrganizationDialog}>
                  <StyledListItemIcon>
                    <BusinessIcon />
                  </StyledListItemIcon>
                  <StyledListItemText
                    primary={
                      <FormattedMessage
                        id='app.components.app-drawer.organization'
                        defaultMessage='Organization'
                      />
                    }
                    secondary={selectedOrganization?.name}
                  />
                </ListItemButton>
              </ListItem>
            </List>

            <Divider />
          </>
        )}

        {/* Legal Section */}
        <SectionHeader>
          <FormattedMessage
            id='app.components.app-drawer.legal'
            defaultMessage='Legal'
          />
        </SectionHeader>

        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleOpenPrivacyPolicy}>
              <StyledListItemIcon>
                <PrivacyTipIcon />
              </StyledListItemIcon>
              <StyledListItemText
                primary={
                  <FormattedMessage
                    id='app.components.app-drawer.privacy-policy'
                    defaultMessage='Privacy Policy'
                  />
                }
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider />

        {/* Account Section */}
        <SectionHeader>
          <FormattedMessage
            id='app.components.app-drawer.account'
            defaultMessage='Account'
          />
        </SectionHeader>

        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <StyledListItemIcon>
                <LogoutIcon />
              </StyledListItemIcon>
              <StyledListItemText
                primary={
                  <FormattedMessage
                    id='app.components.app-drawer.logout'
                    defaultMessage='Logout'
                  />
                }
              />
            </ListItemButton>
          </ListItem>
        </List>
      </DrawerContent>
    </Drawer>

    {organizationDialog}
    {printerDialog}
    </>
  )
}

export default AppDrawer
